import { faWarning } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { FontAwesomeIcon } from 'atoms/icons';
import { Progress } from 'atoms/loader/Progress';

type LayoutProps = { horizontal?: boolean; small?: boolean };

const Label = styled.span`
  grid-area: label;
  white-space: nowrap;
`;

const Secondary = styled.span`
  opacity: 0.5;
  white-space: nowrap;
`;

const AveragePoints = styled.div`
  grid-area: avg;
  display: flex;
  gap: var(--half-unit);
  transition: opacity 0.25s ease-in;
`;

const Wrapper = styled.div<LayoutProps>`
  display: grid;
  grid-template-areas:
    'label points'
    'capbar capbar'
    'avg avg';
  grid-template-columns: 1fr max-content;
  grid-template-rows: 1fr 1fr auto;
  align-items: center;
  font: 500 var(--t-14-16);
  ${({ horizontal }) =>
    horizontal &&
    css`
      grid-template-areas: 'label capbar points';
      grid-template-columns: max-content 1fr min-content;
      grid-template-rows: 1fr;
      gap: var(--double-unit);
    `};

  ${({ small }) =>
    small &&
    css`
      text-align: right;
      ${Label} {
        display: none;
      }
      ${Secondary} {
        display: none;
      }
      ${AveragePoints} {
        justify-content: flex-end;
      }
    `};
`;

const ProgressStyled = styled(Progress)<{ $error: boolean }>`
  grid-area: capbar;
  margin: var(--unit) 0;
  ${props =>
    props.$error &&
    `--color: var(--c-static-red-300);
    --backgroundColor: var(--c-static-red-300);
    `}
`;

const Error = styled(Label)`
  color: var(--c-static-red-300);
`;

const Points = ({
  value,
  total,
  title,
}: {
  value: number;
  total: number;
  title?: string;
}) => {
  return (
    <div title={title}>
      {value >= 0 ? <span>{value}</span> : <Error>{value}</Error>}
      <Secondary>/{total}</Secondary>
    </div>
  );
};

export const calculateScoreTotal = (
  scores: (number | undefined)[],
  allowMVP: boolean
) => {
  const cardScores = scores.map(s => s ?? 0);
  const max = Math.max(...cardScores);
  return (
    cardScores.reduce((sum: number, current) => sum + (current ?? 0), 0) -
    (allowMVP ? max : 0)
  );
};

type Props = {
  cap: number;
  used: number;
  nbEmptySlots?: number;
  className?: string;
} & LayoutProps;

export const CapBar = ({
  cap,
  used,
  nbEmptySlots,
  horizontal,
  className,
  small = false,
}: Props) => {
  const { formatMessage } = useIntl();
  const averagePointsRemaining =
    nbEmptySlots === 0 || nbEmptySlots === undefined
      ? 0
      : Math.max(0, cap - used) / nbEmptySlots;

  return (
    <Wrapper className={className} horizontal={horizontal} small={small}>
      {used > cap && (
        <>
          <Error>
            <FontAwesomeIcon icon={faWarning} />{' '}
            <FormattedMessage
              id="capBar.error"
              defaultMessage="Points cap exceeded"
            />
          </Error>
          <Points value={cap - used} total={cap} />
        </>
      )}

      {used <= cap && (
        <>
          <Label>
            <FormattedMessage
              id="capBar.title"
              defaultMessage="Remaining points"
            />
          </Label>
          <Points
            value={cap - used}
            total={cap}
            title={formatMessage({
              id: 'capbar.budget.title',
              defaultMessage: 'Remaining budget points',
            })}
          />
        </>
      )}

      {nbEmptySlots !== undefined && (
        <AveragePoints style={{ opacity: averagePointsRemaining > 0 ? 1 : 0 }}>
          {small ? (
            <>{nbEmptySlots} ⤫ </>
          ) : (
            <Secondary>
              <FormattedMessage
                id="capbar.average"
                defaultMessage="Average per open position ({nbEmptySlots}):"
                values={{
                  nbEmptySlots,
                }}
              />
            </Secondary>
          )}

          <span
            title={formatMessage({
              id: 'capbar.average.title',
              defaultMessage: 'Average points remaining per open position',
            })}
          >
            {Math.floor(averagePointsRemaining)}
          </span>
        </AveragePoints>
      )}

      <ProgressStyled
        min={0}
        max={cap}
        value={cap - used}
        skipStartAnimation
        $error={used > cap}
      />
    </Wrapper>
  );
};
